// RenameItemDialog — single-input dialog for renaming an explorer folder or
// leaf row. Pass `rewriteLeafPath` so the last path segment tracks the name
// (same slug rules as create).

import { useEffect, useState } from 'react'

import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import type { ExplorerItemKind } from './item-kind'
import { joinChildPath, parentPathOf } from './tree'
import { readMutationError } from './mutation-error'

export type RenameItemKind = ExplorerItemKind

export interface RenameItemDialogProps {
  open: boolean
  kind: RenameItemKind
  /** Override for the folder noun in copy. Defaults to "folder". */
  folderNoun?: string
  currentName: string
  path: string
  /**
   * When true, submit also sends a rewritten leaf path derived from the
   * new name. Every explorer that stores its hierarchy in `path` uses this.
   */
  rewriteLeafPath?: boolean
  onClose: () => void
  onSubmit: (input: { name: string; path?: string }) => Promise<void>
}

const RenameItemDialog = ({
  open,
  kind,
  folderNoun = 'folder',
  currentName,
  path,
  rewriteLeafPath = false,
  onClose,
  onSubmit
}: RenameItemDialogProps) => {
  const [name, setName] = useState(currentName)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setName(currentName)
      setError(null)
      setSubmitting(false)
    }
  }, [open, currentName, path])

  const trimmed = name.trim()
  const noun = kind === 'folder' ? folderNoun : kind
  const title = `Rename ${noun}`
  const unchanged = trimmed === currentName.trim()
  const previewPath = rewriteLeafPath ? joinChildPath(parentPathOf(path), name) : path

  const handleSubmit = async () => {
    if (trimmed === '') {
      setError('Name is required.')
      return
    }
    if (rewriteLeafPath && previewPath === '') {
      setError('Name must contain at least one letter, digit, dash, underscore, or dot.')
      return
    }
    if (unchanged && previewPath === path) {
      onClose()
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit(
        rewriteLeafPath && previewPath !== path
          ? { name: trimmed, path: previewPath }
          : { name: trimmed }
      )
      onClose()
    } catch (err) {
      setError(readMutationError(err))
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (submitting) return
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? handleClose() : undefined)}>
      <DialogContent className="sm:max-w-110" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="explorer-rename-name">Name</Label>
            <Input
              id="explorer-rename-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void handleSubmit()
                }
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-11 uppercase tracking-wider text-ink-3">Path</Label>
            <code className="rounded bg-bg-sunk px-2 py-1 font-mono text-12 text-ink-2">
              /{previewPath || '…'}
            </code>
            <span className="text-11 text-ink-3">
              {rewriteLeafPath
                ? 'The last path segment is derived from the name, same as create.'
                : 'Renaming only changes the display name; the path stays put.'}
            </span>
          </div>
          {error ? (
            <div className="rounded-md bg-st-down-bg p-2.5 text-12 text-st-down" role="alert">
              {error}
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting || trimmed === ''}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default RenameItemDialog
